import { useCallback, useEffect, useMemo, useState } from 'react'
import moment from 'moment-timezone'
import cn from 'classnames'

import { api } from 'app/api'
import HistoryModal, { type RenderCellContent } from 'app/common/ui-components/history-modal'
import { OpenFilterButton } from 'app/common/ui-next/plain-button'
import PlainButtonAsLink from 'app/common/ui-next/plain-button-as-link'
import PlainPaging from 'app/common/ui-next/plain-paging'
import ControlsBar from 'app/common/ui-next/controls-bar'
import FiltersDropDownLayout from 'app/common/layouts/FiltersDropDownLayout'
import type { ComplexHistoryItem, ComplexLastChange } from 'app/common/types/food/schools/complexes/history'
import { ComplexChangeAction } from 'app/common/types/food/schools/complexes/history'

import {
  complexHistoryOperationsFilterSelection,
  periodFilter,
} from 'app/dataProviders/filter-items'

import {
  GET_COMPLEX_CHANGES,
  convertParams,
  convertResult,
} from 'app/common/api/requests/food/schools/complexes/changes/get'

import { Filters, resetToDefaultAll } from './filters'
import styles from './ComplexHistoryLink.module.scss'

const PER_PAGE = 10
const DISPLAY_DATE_FORMAT = 'DD.MM.YYYY'
const API_DATE_FORMAT = 'YYYY-MM-DD'

type HistoryFilters = {
  actionId?: ComplexChangeAction
  dateFrom: string
  dateTo: string
}

const getDefaultFilters = (): HistoryFilters => ({ dateFrom: '', dateTo: '' })

type Props = {
  orgId: number | string
  complexId: number | string
  complexTitle: string
  lastChange?: ComplexLastChange
  className?: string
}

const renderCellContent: RenderCellContent = (columnId, sourceItem) => {
  const item = sourceItem as ComplexHistoryItem
  if (columnId === 'oldValue' || columnId === 'newValue') {
    return <span className={styles.multiline}>{item[columnId]}</span>
  }
  return item[columnId as keyof ComplexHistoryItem] as string
}

const toApiDate = (value: string): string | undefined => {
  if (!value) return undefined
  const date = moment(value, DISPLAY_DATE_FORMAT, true)
  return date.isValid() ? date.format(API_DATE_FORMAT) : undefined
}

const formatLastChangeDate = (value: string): string => {
  const date = moment(value)
  return date.isValid() ? date.format('DD.MM.YYYY HH:mm') : value
}

const ComplexHistoryLinkContent = ({
  orgId,
  complexId,
  complexTitle,
  lastChange,
  className,
}: Props) => {
  const [items, setItems] = useState<Array<ComplexHistoryItem>>([])
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [modalDisplayed, setModalDisplayed] = useState(false)
  const [filtersDisplayed, setFiltersDisplayed] = useState(false)
  const [page, setPage] = useState(0)
  const [filters, setFilters] = useState<HistoryFilters>(getDefaultFilters)

  const request = useCallback(async () => {
    setLoading(true)
    const result = await api.request(GET_COMPLEX_CHANGES, {
      error: `Не удалось получить историю операций по комплекту «${complexTitle}»`,
      params: {
        orgId,
        complexId,
        limit: PER_PAGE,
        offset: page * PER_PAGE,
        actionId: filters.actionId,
        dateFrom: toApiDate(filters.dateFrom),
        dateTo: toApiDate(filters.dateTo),
      },
      convertions: {
        convertParams,
        convertResult,
      },
    })
    if (result) {
      setItems(result.items)
      setCount(result.count)
    }
    setLoading(false)
  }, [complexId, complexTitle, filters, orgId, page])

  useEffect(() => {
    if (modalDisplayed) request()
  }, [modalDisplayed, request])

  const toggleFiltersDisplayed = useCallback(() => {
    setFiltersDisplayed((current) => !current)
  }, [])

  const handleApply = useCallback(() => {
    const [operation] = complexHistoryOperationsFilterSelection.data
    const [dateFrom = '', dateTo = ''] = periodFilter.data

    setPage(0)
    setFilters({
      actionId: operation ? (Number(operation) as ComplexChangeAction) : undefined,
      dateFrom,
      dateTo,
    })
  }, [])

  const handleReset = useCallback(() => {
    resetToDefaultAll()
    setPage(0)
    setFilters(getDefaultFilters())
  }, [])

  const filtersApplied = Boolean(filters.actionId || filters.dateFrom || filters.dateTo)

  const columns = useMemo(
    () => ({
      ids: ['operation', 'date', 'oldValue', 'newValue', 'user'],
      names: [
        'Операция',
        'Дата операции',
        'Старое значение',
        'Новое значение',
        'Пользователь, внесший изменения',
      ],
    }),
    [],
  )

  if (!lastChange) return null

  return (
    <HistoryModal
      className={styles.modal}
      title={`История изменений комплекта — ${complexTitle}`}
      loading={loading}
      columnIdsList={columns.ids}
      columnNamesList={columns.names}
      columnPropsList={columns.ids.map(() => ({ alignLeft: true }))}
      itemsList={items}
      renderCellContent={renderCellContent}
      emptyWarnMessage="Отсутствуют записи в истории операций"
      mainContentMinHeight="40vh"
      mainContentMaxHeight="40vh"
      onDisplayStatusChange={setModalDisplayed}
      headerContentClassName={styles.filtersContent}
      headerContent={
        <>
          <ControlsBar className={styles.filtersToggle}>
            <OpenFilterButton
              checked={filtersApplied}
              opened={filtersDisplayed}
              disabled={loading}
              onClick={toggleFiltersDisplayed}
            />
          </ControlsBar>

          {filtersDisplayed && (
            <FiltersDropDownLayout
              className={styles.filtersPanel}
              contentClassName={styles.filtersContentInner}
              loading={loading}
              wide
              wideOrientation
              onClose={toggleFiltersDisplayed}
              onApplyFilter={handleApply}
              onResetAll={handleReset}
            >
              {(resetStatus) => (
                <Filters
                  pending={loading}
                  resetStatus={resetStatus}
                  defaultOperation={filters.actionId ? [String(filters.actionId)] : []}
                  defaultPeriod={[filters.dateFrom, filters.dateTo]}
                />
              )}
            </FiltersDropDownLayout>
          )}
        </>
      }
      footerContent={
        <PlainPaging
          total={count}
          perPage={PER_PAGE}
          currentPageIndex={page}
          currentPageSize={items.length}
          onPage={setPage}
        />
      }
    >
      {(toggle) => (
        <PlainButtonAsLink className={cn(styles.link, className)} onClick={toggle}>
          {'Последние изменения: '}
          {/* отключено <strong>{lastChange.login}</strong> */}
          {` ${formatLastChangeDate(lastChange.datetime)}`}
        </PlainButtonAsLink>
      )}
    </HistoryModal>
  )
}

export const ComplexHistoryLink = ComplexHistoryLinkContent
